import type { Circuit } from '@multirx/shared/types/track.js';
import type { PlayerTrackPosition } from '@multirx/shared/types/track.js';
import { getPlayerTrackCell } from '@multirx/shared/game/jokerLap.js';
import { getTrackForwardDelta } from '@multirx/shared/game/trackMovement.js';
import Phaser from 'phaser';
import { getCircuitVehicleSpritePath } from '@multirx/shared/game/circuitVehicleCatalog.js';
import { decorVehicleDisplaySize } from '@multirx/shared/game/circuitPropDraw.js';
import {
  calculateTrackOffset,
  PhaserTrackDrawSurface,
  renderCircuit,
  type GridStyle,
} from './trackCircuitRender.js';

function vehicleTextureKey(spriteId: string): string {
  return `circuit-decor-vehicle-${spriteId}`;
}

export type { GridStyle } from './trackCircuitRender.js';

/**
 * Rendu visuel d'un circuit dans Phaser — contour épais + séparation des cases (configurable)
 */
export class TrackRenderer {
  private graphics: Phaser.GameObjects.Graphics | null = null;
  private signTexts: Phaser.GameObjects.Text[] = [];
  private vehicleSprites: Phaser.GameObjects.Image[] = [];
  private cellSize: number = 20;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private gridStyle: GridStyle = 'checker';

  constructor(
    private readonly scene: Phaser.Scene,
    cellSize: number = 20
  ) {
    this.cellSize = cellSize;
    this.graphics = scene.add.graphics();
  }

  public setCellSize(size: number): void {
    this.cellSize = size;
  }

  public setGridStyle(style: GridStyle): void {
    this.gridStyle = style;
  }

  public setOffset(x: number, y: number): void {
    this.offsetX = x;
    this.offsetY = y;
  }

  public calculateOffset(circuit: Circuit, screenWidth: number, screenHeight: number): void {
    const { offsetX, offsetY } = calculateTrackOffset(
      circuit,
      this.cellSize,
      screenWidth,
      screenHeight
    );
    this.offsetX = offsetX;
    this.offsetY = offsetY;
  }

  public getOffset(): { x: number; y: number } {
    return { x: this.offsetX, y: this.offsetY };
  }

  public getCellSize(): number {
    return this.cellSize;
  }

  public setDepth(depth: number): void {
    this.graphics?.setDepth(depth);
    for (const label of this.signTexts) {
      label.setDepth(depth);
    }
    for (const sprite of this.vehicleSprites) {
      sprite.setDepth(depth);
    }
  }

  private clearSignTexts(): void {
    for (const label of this.signTexts) {
      label.destroy();
    }
    this.signTexts = [];
  }

  private clearVehicleSprites(): void {
    for (const sprite of this.vehicleSprites) {
      sprite.destroy();
    }
    this.vehicleSprites = [];
  }

  private renderVehicleSprites(circuit: Circuit): void {
    this.clearVehicleSprites();
    const vehicles = circuit.vehicles ?? [];
    if (!vehicles.length) return;

    const depth = this.graphics?.depth ?? 0;
    const missing = vehicles.filter(
      (vehicle) => !this.scene.textures.exists(vehicleTextureKey(vehicle.spriteId))
    );

    if (missing.length > 0) {
      for (const vehicle of missing) {
        const path = getCircuitVehicleSpritePath(vehicle.spriteId);
        if (!path) continue;
        this.scene.load.image(vehicleTextureKey(vehicle.spriteId), path);
      }
      if (this.scene.load.list.size > 0) {
        this.scene.load.once('complete', () => {
          if (this.graphics) this.render(circuit);
        });
        this.scene.load.start();
      }
      return;
    }

    const size = decorVehicleDisplaySize(this.cellSize);
    for (const vehicle of vehicles) {
      const key = vehicleTextureKey(vehicle.spriteId);
      if (!this.scene.textures.exists(key)) continue;
      const x = this.offsetX + vehicle.x * this.cellSize + this.cellSize / 2;
      const y = this.offsetY + vehicle.y * this.cellSize + this.cellSize / 2;
      const sprite = this.scene.add.image(x, y, key).setOrigin(0.5).setDepth(depth);
      sprite.setDisplaySize(size, size);
      sprite.setAlpha(1);
      this.vehicleSprites.push(sprite);
    }
  }

  private renderSignTexts(circuit: Circuit): void {
    this.clearSignTexts();
    const depth = this.graphics?.depth ?? 0;
    const fontSize = Math.max(10, Math.round(this.cellSize * 0.22));
    const pad = this.cellSize * 0.12;
    for (const sign of circuit.signs ?? []) {
      const x = this.offsetX + sign.x * this.cellSize;
      const y = this.offsetY + sign.y * this.cellSize;
      const w = sign.width * this.cellSize;
      const h = sign.height * this.cellSize;
      const label = this.scene.add
        .text(x + w / 2, y + h / 2, sign.text, {
          fontFamily: 'Inter, Arial, sans-serif',
          fontSize: `${fontSize}px`,
          fontStyle: '600',
          color: '#422006',
          align: 'center',
          wordWrap: { width: Math.max(24, w - pad * 2) },
        })
        .setOrigin(0.5)
        .setDepth(depth);
      this.signTexts.push(label);
    }
  }

  public getPositionForPathIndex(circuit: Circuit, pathIndex: number): { x: number; y: number } {
    if (!circuit.path.length) {
      return { x: this.offsetX, y: this.offsetY };
    }
    const len = circuit.path.length;
    const i = ((pathIndex % len) + len) % len;
    const cell = circuit.path[i];
    const x = this.offsetX + cell.x * this.cellSize + this.cellSize / 2;
    const y = this.offsetY + cell.y * this.cellSize + this.cellSize / 2;
    return { x, y };
  }

  public getPositionForTrackPosition(
    circuit: Circuit,
    position: PlayerTrackPosition
  ): { x: number; y: number } {
    const cell = getPlayerTrackCell(circuit, position);
    if (!cell) {
      return { x: this.offsetX, y: this.offsetY };
    }
    return {
      x: this.offsetX + cell.x * this.cellSize + this.cellSize / 2,
      y: this.offsetY + cell.y * this.cellSize + this.cellSize / 2,
    };
  }

  public getForwardDeltaForTrackPosition(
    circuit: Circuit,
    position: PlayerTrackPosition
  ): { dx: number; dy: number } {
    return getTrackForwardDelta(circuit, position);
  }

  public render(circuit: Circuit): void {
    if (!this.graphics || circuit.path.length === 0) {
      return;
    }

    this.graphics.clear();

    const surface = new PhaserTrackDrawSurface(this.graphics);
    renderCircuit(surface, circuit, {
      offsetX: this.offsetX,
      offsetY: this.offsetY,
      cellSize: this.cellSize,
      gridStyle: this.gridStyle,
      drawVehicles: false,
    });
    this.renderSignTexts(circuit);
    this.renderVehicleSprites(circuit);
  }

  public destroy(): void {
    this.clearVehicleSprites();
    this.clearSignTexts();
    if (this.graphics) {
      this.graphics.destroy();
      this.graphics = null;
    }
  }
}
